import { Box, Button, Center, LoadingOverlay, Stack, Text } from '@mantine/core'
import { IconUpload } from '@tabler/icons-react'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getApiError } from '@/api/helpers/apiError'
import { useCreateCameras } from '@/queries/cameraQueries'
import { useGetDeviceDetails } from '@/queries/deviceQueries'
import { showToast } from '@/theme/notifications'
import { CameraSource, ImportCameraData } from '@/types/camera'
import { Device } from '@/types/device'
import { getAugmentedCameras } from '@/utils/device/getAugmentedCameras'
import { CameraSelectionList } from '../CameraSelectionList/CameraSelectionList'

const buildAlreadyImportedCameras = (
  deviceData: Device | undefined
): ImportCameraData[] => {
  const milestoneCameras = (
    deviceData !== undefined ? getAugmentedCameras(deviceData) : []
  ).filter((camera) => camera.source === CameraSource.Milestone)

  return milestoneCameras.map((camera) => ({
    id: camera.attributes?.milestone_camera_id || '',
    name: camera.camera_name,
    rtsp: {
      id: camera.attributes?.milestone_stream_ids[0] || '',
      url: '',
      image: null,
      thumbnail: null
    }
  }))
}

type CameraSelectionHandlerProps = {
  deviceId: string
  cameras: ImportCameraData[]
  onImportStart: () => void
  onBack: () => void
}

export const CameraSelectionHandler = ({
  deviceId,
  cameras,
  onImportStart,
  onBack
}: CameraSelectionHandlerProps) => {
  const intl = useIntl()

  const [selectedCameras, setSelectedCameras] = useState<ImportCameraData[]>([])

  const { mutateAsync: importCameras, isPending: isImportPending } =
    useCreateCameras()

  const { data: deviceData, isFetching: isFetchingDeviceData } =
    useGetDeviceDetails(deviceId)

  const alreadyImportedCameras = buildAlreadyImportedCameras(deviceData).sort(
    (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })
  )

  const camerasToImport = cameras
    .filter(
      (camera) =>
        !alreadyImportedCameras.some(
          (importedCamera) => importedCamera.id === camera.id
        )
    )
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))

  const handleImportClick = async () => {
    const importData = selectedCameras.map((camera) => ({
      camera_name: camera.name,
      rtsp_url: camera.rtsp.url,
      source: CameraSource.Milestone,
      attributes: {
        milestone_camera_id: camera.id,
        milestone_stream_ids: [camera.rtsp.id]
      }
    }))

    try {
      await importCameras({
        deviceId,
        cameras: importData
      })

      onImportStart()
    } catch (err) {
      const { errorMessage } = getApiError(err)

      const message =
        errorMessage || intl.formatMessage({ id: 'cameras.import.error' })

      showToast(message, 'error')
    }
  }

  return (
    <Box pos="relative" mih={120}>
      <LoadingOverlay visible={isFetchingDeviceData || isImportPending} />

      {cameras.length > 0 && (
        <>
          <CameraSelectionList
            selectedCameras={selectedCameras}
            camerasToImport={camerasToImport}
            alreadyImportedCameras={alreadyImportedCameras}
            onSelectionChange={setSelectedCameras}
          />

          <Center mt="lg">
            <Button
              disabled={selectedCameras.length === 0}
              leftSection={<IconUpload size={16} />}
              onClick={() => void handleImportClick()}
            >
              <FormattedMessage
                id="cameras.import.importSelected"
                values={{
                  count: selectedCameras.length
                }}
              />
            </Button>
          </Center>
        </>
      )}

      {cameras.length === 0 && (
        <Stack align="center">
          <Text size="sm" ta="center">
            <FormattedMessage id="cameras.import.milestone.noCameras" />
          </Text>

          <Box>
            <Button size="xs" variant="outline" miw={120} onClick={onBack}>
              <FormattedMessage id="goBack" />
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  )
}
